import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/styles';
import { CheckCircle, ExclamationOctagon } from 'react-bootstrap-icons';
import FormSectionCard from '../../components/FormSectionCard';
import EmailRedirectHint from '../../components/EmailRedirectHint';
import FormSubmitOverlay, {
	FORM_SUBMIT_OVERLAY_MODES,
} from '../../components/FormSubmitOverlay';
import AlertModal from '../../components/Alert';
import { normalizeContactsResponse } from '../../services/contacts';
import { useCurrentUser } from '../../authentication/useAuth';
import { useEmailService } from '../../services/email/useEmailService';
import { useContactApi } from '../../apis';
import { isRequiredTrimmed } from '../../utils/validation';
import { ContactUsRequestTemplate } from '../../utils/forms';

const DEFAULT_FORM_VALUES = {
	plantLocation: '',
	emailTo: '',
	recipientLabel: '',
	subject: '',
	message: '',
};

function ContactUs() {
	const theme = useTheme();
	const { t, i18n } = useTranslation('contact_us', 'common');
	const [submitStatus, setSubmitStatus] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [contactsLoadError, setContactsLoadError] = useState('');
	const [plantOptions, setPlantOptions] = useState([]);
	const [contactsByPlant, setContactsByPlant] = useState({});
	const currentUser = useCurrentUser();
	const userEmail = currentUser.email || '';
	const { sendEmail, actualUserEmail, isEmailRedirectEnabled } =
		useEmailService();

	const { getCsrs: getContacts } = useContactApi();
	const { control, handleSubmit, reset, setValue, formState } = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: DEFAULT_FORM_VALUES,
	});

	const selectedPlantName = useWatch({
		control,
		name: 'plantLocation',
	});

	const selectedPlantContacts = selectedPlantName
		? contactsByPlant[selectedPlantName] || []
		: [];

	const isSubmitDisabled = !formState.isValid || isSubmitting;
	const showRecipientRedirectHint =
		isEmailRedirectEnabled && Boolean(actualUserEmail);

	useEffect(() => {
		/**
		 * Fetches contact records and normalizes them into:
		 * 1) a sorted plant list for dropdown 1
		 * 2) a plant-keyed CSR list for dropdown 2
		 *
		 * Supports both current proxy payload shape and direct-array responses.
		 */
		async function fetchContacts() {
			setContactsLoadError('');
			try {
				const response = await getContacts();
				if (response?.isAborted) {
					return;
				}

				if (response?.isError) {
					throw response.error || new Error('Failed to load contacts');
				}

				const normalizedContacts = normalizeContactsResponse(response);
				setPlantOptions(normalizedContacts.plantOptions);
				setContactsByPlant(normalizedContacts.contactsByPlant);
			} catch (error) {
				setPlantOptions([]);
				setContactsByPlant({});
				setContactsLoadError(t('error'));
			}
		}

		fetchContacts();
	}, [getContacts, t]);

	/**
	 * Required-field validator that treats whitespace-only values as empty.
	 */
	function validateRequiredTrimmed(value) {
		return isRequiredTrimmed(value) ? true : t('blank');
	}

	/**
	 * Sends the Contact Us request.
	 * Resets form state on success and surfaces an error banner on failure.
	 */
	async function onSubmit(values) {
		setSubmitStatus('');
		setIsSubmitting(true);

		const trimmedSubject = values.subject ? values.subject.trim() : '';
		const trimmedMessage = values.message ? values.message.trim() : '';

		const response = await sendEmail({
			to: values.emailTo,
			locale: i18n.resolvedLanguage || i18n.language,
			subject: trimmedSubject,
			templateName: ContactUsRequestTemplate(),
			customized: {
				submittedByFirstName: currentUser?.firstName,
				submittedByLastName: currentUser?.lastName,
				replyToEmail: currentUser?.email,
				recipientName: values.recipientLabel || values.emailTo,
				reference: trimmedSubject,
				message: trimmedMessage,
			},
		});

		if (response?.isAborted) {
			setIsSubmitting(false);
			return;
		}

		if (response?.isError) {
			setSubmitStatus('error');
			setIsSubmitting(false);
			return;
		}

		setSubmitStatus('success');
		reset(DEFAULT_FORM_VALUES);
		setIsSubmitting(false);
	}

	function onInvalidSubmit() {
		// react-hook-form blocks submission automatically when invalid.
	}

	function handleCloseStatusModal() {
		setSubmitStatus('');
	}

	/**
	 * Shared form-label renderer with optional required marker.
	 */
	function renderLabel(label, required = false) {
		return (
			<Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
				{label}
				{required && (
					<Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
						*
					</Box>
				)}
			</Typography>
		);
	}

	return (
		<Container maxWidth="md" aria-label={t('contact_us')}>
			<Grid
				container
				direction="column"
				alignItems="flex-start"
				sx={{ mt: 6, mb: 4 }}
			>
				<Grid item xs={12} sx={{ width: '100%', textAlign: 'left' }}>
					<Typography
						variant="heading1"
						aria-label={t('contact_us')}
						sx={{ fontSize: { xs: '1.9rem', sm: '2.2rem' }, lineHeight: 1.2 }}
					>
						{t('contact_us')}
					</Typography>
				</Grid>
				<Grid item xs={12} sx={{ width: '100%', textAlign: 'left', mt: 1 }}>
					<Typography
						aria-label={t('subtitle')}
						variant="heading4"
						color="textSecondary"
						sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, lineHeight: 1.4 }}
					>
						{t('subtitle')}
					</Typography>
				</Grid>
			</Grid>
			<FormSectionCard>
				{contactsLoadError && (
					<Typography color="error" sx={{ width: '100%', mb: 2 }}>
						{contactsLoadError}
					</Typography>
				)}
				<form
					onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
					autoComplete="off"
				>
					<Grid container spacing={2.5}>
						<Grid item xs={12}>
							{renderLabel(t('plantLocation', { ns: 'common' }), true)}
							<Controller
								name="plantLocation"
								control={control}
								rules={{ required: t('blank') }}
								render={({ field, fieldState }) => (
									<TextField
										fullWidth
										select
										id="plant-location"
										{...field}
										onChange={(event) => {
											field.onChange(event);
											setValue('emailTo', '', {
												shouldDirty: true,
												shouldValidate: true,
											});
											setValue('recipientLabel', '', {
												shouldDirty: true,
												shouldValidate: false,
											});
										}}
										error={Boolean(fieldState.error)}
										helperText={
											fieldState.error ? fieldState.error.message : ''
										}
									>
										<MenuItem value="" />
										{plantOptions.map((plantName) => (
											<MenuItem key={plantName} value={plantName}>
												{plantName}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							{renderLabel(t('email_fields.to'), true)}
							<Controller
								name="emailTo"
								control={control}
								rules={{ required: t('blank') }}
								render={({ field, fieldState }) => (
									<TextField
										fullWidth
										select
										id="email-to"
										{...field}
										onChange={(event) => {
											const selectedEmail = event.target.value;
											field.onChange(event);

											const selectedContact = selectedPlantContacts.find(
												(contact) => contact.email === selectedEmail
											);

											setValue('recipientLabel', selectedContact?.label || '', {
												shouldDirty: true,
												shouldValidate: false,
											});
										}}
										disabled={
											!selectedPlantName || !selectedPlantContacts.length
										}
										error={Boolean(fieldState.error)}
										helperText={
											fieldState.error ? fieldState.error.message : ''
										}
									>
										<MenuItem value="" />
										{selectedPlantContacts.map((contact) => (
											<MenuItem
												key={`${contact.email}-${contact.label}`}
												value={contact.email}
											>
												{contact.label}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							{renderLabel(t('email_fields.from'))}
							<TextField
								fullWidth
								id="contact-from"
								value={userEmail}
								disabled
								autoComplete="off"
							/>
						</Grid>
						<Grid item xs={12}>
							{renderLabel(t('email_fields.subject'), true)}
							<Controller
								name="subject"
								control={control}
								rules={{ validate: validateRequiredTrimmed }}
								render={({ field, fieldState }) => (
									<TextField
										fullWidth
										id="contact-subject"
										{...field}
										error={Boolean(fieldState.error)}
										helperText={
											fieldState.error ? fieldState.error.message : ''
										}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							{renderLabel(t('email_fields.message'), true)}
							<Typography
								variant="caption"
								color="textSecondary"
								sx={{ display: 'block', mb: 0.75 }}
							>
								{t('message_order_number_hint')}
							</Typography>
							<Controller
								name="message"
								control={control}
								rules={{ validate: validateRequiredTrimmed }}
								render={({ field, fieldState }) => (
									<TextField
										fullWidth
										id="contact-message"
										multiline
										rows={5}
										{...field}
										error={Boolean(fieldState.error)}
										helperText={
											fieldState.error ? fieldState.error.message : ''
										}
										sx={{
											'& .MuiInputBase-inputMultiline': {
												resize: 'vertical',
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid
							item
							xs={12}
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								size="large"
								disabled={isSubmitDisabled}
								sx={{ minWidth: 150 }}
							>
								<EmailRedirectHint
									show={showRecipientRedirectHint}
									actualUserEmail={actualUserEmail}
									inline
								/>
								{t('submit', { ns: 'common' })}
							</Button>
						</Grid>
					</Grid>
				</form>
			</FormSectionCard>
			<AlertModal
				icon={
					submitStatus === 'success' ? (
						<CheckCircle
							style={{ color: theme.palette.success.main, fontSize: '2rem' }}
						/>
					) : (
						<ExclamationOctagon
							style={{ color: theme.palette.danger.main, fontSize: '2rem' }}
						/>
					)
				}
				border={
					submitStatus === 'success'
						? `1px solid ${theme.palette.success.border}`
						: `1px solid ${theme.palette.danger.border}`
				}
				title={submitStatus === 'success' ? t('alert') : t('error_modal_title')}
				subtitleOne={
					submitStatus === 'success' ? t('success_modal_body') : t('error')
				}
				subtitleTwo={null}
				open={Boolean(submitStatus)}
				onClose={handleCloseStatusModal}
			/>
			<FormSubmitOverlay
				open={isSubmitting}
				mode={FORM_SUBMIT_OVERLAY_MODES.PAGE_FORM}
			/>
		</Container>
	);
}

export default ContactUs;
